import React, { useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NewsletterSignup = ({ variant = 'default', className = '' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer' })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Successfully subscribed!');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Connection error. Please try again.');
    }
  };

  if (variant === 'inline') {
    return (
      <div className={`${className}`} data-testid="newsletter-signup-inline">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              disabled={status === 'loading' || status === 'success'}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border text-sm focus:outline-none focus:border-foreground disabled:opacity-50"
              data-testid="newsletter-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            className="px-4 py-2 bg-foreground text-background text-xs uppercase tracking-widest font-medium hover:bg-foreground/90 disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="newsletter-submit-btn"
          >
            {status === 'loading' ? (
              <Loader2 size={14} className="animate-spin" />
            ) : status === 'success' ? (
              <Check size={14} />
            ) : (
              'Subscribe'
            )}
          </button>
        </form>
        {message && (
          <p className={`text-xs mt-2 ${status === 'error' ? 'text-red-500' : 'text-green-500'}`}>
            {message}
          </p>
        )}
      </div>
    );
  }

  // Default variant - full width block
  return (
    <div className={`py-12 ${className}`} data-testid="newsletter-signup">
      <div className="text-center mb-6">
        <h3 className="text-lg editorial-serif mb-2">Stay Updated</h3>
        <p className="text-sm text-muted-foreground">
          Get the latest on creator-led projects in film, media, and culture.
        </p>
      </div>

      {status === 'success' ? (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-3 bg-green-500/10 text-green-500 text-sm">
            <Check size={16} />
            {message}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="max-w-md mx-auto">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={status === 'loading'}
                className="w-full pl-12 pr-4 py-3 bg-background border border-border text-sm focus:outline-none focus:border-foreground disabled:opacity-50"
                data-testid="newsletter-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-6 py-3 bg-foreground text-background text-xs uppercase tracking-widest font-medium hover:bg-foreground/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              data-testid="newsletter-submit-btn"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Subscribing...
                </>
              ) : (
                'Subscribe'
              )}
            </button>
          </div>
          {status === 'error' && message && (
            <p className="text-red-500 text-xs mt-2 text-center">{message}</p>
          )}
        </form>
      )}
    </div>
  );
};

export default NewsletterSignup;
